import Tabs from "components/lib/Shared/Tabs";
import TransactionReviewTab from "components/TransactionReviewTab";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyDashboardAsync } from "state/slices/companyDashboard";
import { getHistoryCompanyDashboardAsync } from "state/slices/historyCompanyDashboard";
import BusinessDetails from "./BusinessDetails";

const HistoryTransactionReview = (props) => {
  const { transactionUnderReview, getTransactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions } = transactionUnderReview.items;
  const periodTabs = generatePeriodTabs(transactions);
  const [selectedHistoryTransaction, setSelectedHistoryTransaction] =
    useState(null);
  const [selectedPeriodTabIndex, setSelectedPeriodTabIndex] = useState(0);
  const [selectedPeriodTab, setSelectedPeriodTab] = useState(periodTabs[0]);
  const [isLastPeriod, setIsLastPeriod] = useState(false);
  const [isFirstPeriod, setIsFirstPeriod] = useState(true);
  const dispatch = useDispatch()


  const [transactionTabs, setTransactionTabs] = useState(
    generateTransactionTabs(
      transactions.filter((trx) => trx.period === selectedPeriodTab.id)
    )
  );
  const [selectedTransactionTabIndex, setSelectedTransactionTabIndex] =
    useState(0);
  const [selectedTransactionTab, setSelectedTransactionTab] = useState(
    transactionTabs[0]
  );
  const [isLastTransactionInPeriod, setIsLastTransactionInPeriod] = useState(false);
  const [isFirstTransactionInPeriod, setIsFirstTransactionInPeriod] = useState(true);

  const goToNextPeriod = () => {
    setSelectedPeriodTabIndex(prev => prev + 1)
  }

  const goToPreviousPeriod = () => {
    setSelectedPeriodTabIndex(prev => prev - 1)
  }

  const goToNextTransaction = () => {
    setSelectedTransactionTabIndex(prev => prev + 1)
  }

  const goToPreviousTransaction = () => {
    setSelectedTransactionTabIndex(prev => prev - 1)
  }

  const goHistoryNextStep = () => {
    if (!isLastTransactionInPeriod) {
      goToNextTransaction();
    }
    if (isLastTransactionInPeriod && !isLastPeriod) {
      goToNextPeriod();
    }
  }

  const goHistoryPreviousStep = () => {
    if (!isFirstTransactionInPeriod) {
      goToPreviousTransaction();
    }
    if (isFirstTransactionInPeriod && !isFirstPeriod) {
      goToPreviousPeriod();
    }
  }

  useEffect(() => {
    setSelectedPeriodTab(periodTabs[selectedPeriodTabIndex]);
    // we subtract 2 because the last tab is "current" which is not a real period, and the index is 0-based
    if (selectedPeriodTabIndex === periodTabs.length - 2) {
      setIsLastPeriod(true);
    } else {
      setIsLastPeriod(false);
    }
    if (selectedPeriodTabIndex === 0) {
      setIsFirstPeriod(true);
    } else {
      setIsFirstPeriod(false);
    }
  }, [selectedPeriodTabIndex]);

  useEffect(() => {
    const filteredTransactions = transactions.filter(
      (trx) => trx.period === selectedPeriodTab.id
    );
    setTransactionTabs(generateTransactionTabs(filteredTransactions));
  }, [selectedPeriodTab]);

  useEffect(() => {
    setSelectedTransactionTabIndex(0);
  }, [transactionTabs]);

  useEffect(() => {
    setSelectedTransactionTab(transactionTabs[selectedTransactionTabIndex]);
    if (selectedTransactionTabIndex === transactionTabs.length - 1) {
      setIsLastTransactionInPeriod(true);
    } else {
      setIsLastTransactionInPeriod(false);
    }
    if (selectedTransactionTabIndex === 0) {
      setIsFirstTransactionInPeriod(true);
    } else {
      setIsFirstTransactionInPeriod(false);
    }
  }, [selectedTransactionTabIndex, transactionTabs]);

  useEffect(() => {
    setSelectedHistoryTransaction(
      transactions.find(
        (trx) =>
          trx.trxRef === selectedTransactionTab?.id ||
          trx?.batchNo === selectedTransactionTab?.id
      )
    );
  }, [selectedTransactionTab]);

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const companyId = transactions[0].companyId._id;
      dispatch(getHistoryCompanyDashboardAsync({ companyId }));
    }
  }, [transactions])

  return (
    <div>
      <div>
        <h2 className="text-lg font-medium mb-4">Company History</h2>
        <p className="text-sm text-sflPrimary mb-6">
          Update the history of your company, including changes to its
          registered address, share information, and beneficial ownership.
        </p>
      </div>

      {/** Period tabs */}
      <Tabs
        tabs={periodTabs}
        selectedTabIndex={selectedPeriodTabIndex}
        setSelectedTabIndex={setSelectedPeriodTabIndex}
      />
      <div>
        <Tabs
          tabs={transactionTabs}
          selectedTabIndex={selectedTransactionTabIndex}
          setSelectedTabIndex={setSelectedTransactionTabIndex}
        />
      </div>

      {
        selectedPeriodTab.id === "current" ? (
          <BusinessDetails />
        ) :
          <TransactionReviewTab
            {...props}
            historyTransaction={selectedHistoryTransaction}
            isLastPeriod={isLastPeriod}
            isFirstPeriod={isFirstPeriod}
            isLastTransactionInPeriod={isLastTransactionInPeriod}
            isFirstTransactionInPeriod={isFirstTransactionInPeriod}
            goHistoryNextStep={goHistoryNextStep}
            goHistoryPreviousStep={goHistoryPreviousStep}
          />
      }


    </div>
  );
};

function generatePeriodTabs(transactions) {
  const periods = [...new Set(transactions.map((trx) => trx.period))];

  const sorted = periods.sort((a, b) => {
    const startA = parseInt(a.split("/")[1]); // 2025 from "2026/2025"
    const startB = parseInt(b.split("/")[1]);

    return startA - startB; // oldest → latest
  });

  return [
    ...sorted.map((period) => ({
      id: period,
      label: period,
    })),
    {
      id: "current",
      label: "Current",
    }
  ]
}

function generateTransactionTabs(transactions) {
  //Group transactions by batch number or trxRef
  const groupedHistoryTransactions = () => {
    const groups = {};

    transactions?.forEach((transaction) => {
      const batchNo = transaction?.batchNo;
      const trxRef = transaction?.trxRef;

      let groupKey;

      if (batchNo && batchNo.trim() !== "") {
        groupKey = batchNo;
      } else if (trxRef && trxRef.trim() !== "") {
        groupKey = trxRef;
      } else {
        groupKey = "ungrouped-unknown";
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }

      groups[groupKey].push(transaction);
    });
    return groups;
  };

  ///generate tabs for the grouped transactions
  const transactionGroupedTabs = () => {
    return [
      ...Object.keys(groupedHistoryTransactions()).map((key) => {
        const transactions = groupedHistoryTransactions()[key];
        const firstTransaction = transactions[0];
        const label = firstTransaction?.batchNo
          ? `Batch ${firstTransaction.batchNo}`
          : `Ref ${firstTransaction.trxRef}`;

        return {
          id: key,
          label,
          transactions,
        };
      }),
    ];
  };
  return transactionGroupedTabs();
}

export default HistoryTransactionReview;
